import AlertInfo from '@components/alert/info'
import { PageComponent } from 'types/app'
import React, { useContext, useEffect, useState } from 'react'
import { ArticleLayout } from '@components/common'
import { withAnon, withLayout, withProps } from '@utils/hoc'
import { Footer } from '@components/login'
import { CircularProgress, Typography } from '@mui/material'
import { FlatButton } from '@components/ui'
import Link from 'next/link'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useRouter } from 'next/router'
import { RedirectContext } from '@utils/context/redirectContext'
import { isValidPhoneNumber } from 'react-phone-number-input'
import TextField, { SimpleTextField } from '@components/ui/TextField'
import PhoneInput from '@components/ui/PhoneInput'
import routes from '@const/routes'
import { useStore } from '@utils/hooks'
import StyledCheckbox from '@components/ui/Checkbox'
import { PersonTypeEnum } from '@core/global/enums'

interface IFormInputs {
  email: string
  firstname: string
  home_address: object
  lastname: string
  mobile: string
  office_address: object
  organization: string
  password: string
  phone: string
}

const defaultValues: IFormInputs = {
  email: '',
  firstname: '',
  home_address: {},
  lastname: '',
  mobile: '',
  office_address: {},
  organization: '',
  password: '',
  phone: ''
}

type Status = 'loading' | 'ready'

const schema = yup.object().shape({
  lastname: yup
    .string()
    .matches(/.{2,}/, {
      message: 'Le nom doit comporter au moins 2 caractères!'
    })
    .required('Le nom est requis!'),
  firstname: yup
    .string()
    .matches(/.{2,}/, {
      message: 'Le prénom doit comporter au moins 2 caractères!'
    })
    .required('Le prénom est requis!'),
  organization: yup.string(),
  email: yup.string().email('L‘email n‘est pas valide!').required('L‘email est requis!'),
  password: yup
    .string()
    .matches(/.{10,}/, {
      message: 'Le mot de passe doit comporter au moins 10 caractères!'
    })
    .matches(/^(?=.*[a-z])/, 'Le mot de passe doit contenir une minuscule')
    .matches(/^(?=.*[A-Z])/, 'Le mot de passe doit contenir une majuscule')
    .matches(/^(?=.*[0-9])/, 'Le mot de passe doit contenir un chiffre')
    .required('Le mot de passe est requis'),
  mobile: yup
    .string()
    .test({
      name: 'validPhoneNumber',
      message: 'Le numéro est invalide',
      test: value => (value ? isValidPhoneNumber(value) : true)
    })
    .required('Le nom est requis!'),
  phone: yup.string().test({
    name: 'validPhoneNumber',
    message: 'Le numéro est invalide',
    test: value => (value ? isValidPhoneNumber(value) : true)
  }),
  home_address: yup.object(),
  office_address: yup.object()
})

const SignUp: PageComponent = () => {
  const router = useRouter()
  const { store } = useStore()
  const { redirectTo, setRedirectTo } = useContext(RedirectContext)

  const [formValues] = useState<IFormInputs>(defaultValues)
  const [status, setStatus] = useState<Status>('ready')
  const [checked, setChecked] = useState<boolean>(false)

  const { email, firstname, lastname, mobile, organization, password, phone } = formValues

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema)
  })

  const onSubmit: SubmitHandler<IFormInputs> = async data => {
    const postSignup: any = {
      user: {
        username: data.email,
        password: data.password,
        customer: {
          lastName: data.lastname,
          firstName: data.firstname,
          label: data.organization || '',
          email: data.email,
          phoneNumber: data.phone || '',
          mobilePhoneNumber: data.mobile,
          personType: { id: data.organization ? PersonTypeEnum.company : PersonTypeEnum.person }
        }
      }
    }

    try {
      if (store) {
        setStatus('loading')
        store.globalHttpHeaders = { 'X-Tenant-Id': process.env.TENANT_ID }
        await store.meStore.signup(postSignup)
        // @todo force tenant for super-ya because actual account is adman and can choose tenant to loggin
        // @ts-ignore
        store.meStore.tenant = { id: Number(process.env.TENANT_ID), label: 'Super Ya' }
        await store.meStore.login(data.email, data.password)
        if (redirectTo) {
          router.push(redirectTo)
          setRedirectTo(null)
        } else {
          router.push(routes.mon_compte_mon_profil.uri)
        }
      }
    } catch (error) {
      console.log('Error caught : ', error)
    }

    setStatus('ready')
  }

  useEffect(() => {
    for (const error of Object.values<any>(errors)) {
      if (error?.ref) {
        let firstErrorElement = document.getElementsByName(error.ref.name)[0]
        firstErrorElement.scrollIntoView({ behavior: 'smooth' })
        break
      }
    }
  }, [errors])

  return (
    <div className="mx-auto mt-12.5 w-full max-w-[790px] sm:px-[10px] md:w-[790px]">
      <form method="POST" onSubmit={handleSubmit(onSubmit)}>
        <div className="mx-[30px] flex flex-1 flex-col sm:gap-8 mobile:space-y-8">
          <div className="flex flex-col sm:gap-8 md:flex-row mobile:space-y-8">
            <TextField id="lastname" label="Nom *" name="lastname" placeholder="Entrer mon nom" fullWidth control={control} value={lastname} />
            <TextField id="firstname" label="Prénom *" name="firstname" placeholder="Entrer mon prénom" fullWidth control={control} value={firstname} />
          </div>
          <div className="flex flex-col sm:gap-8 md:flex-row mobile:space-y-8">
            <TextField id="organization" label="Société" name="organization" placeholder="Entrer le nom de la société" className="flex-1" control={control} value={organization} />
            <div className="hidden md:block md:flex-1" />
          </div>
          <div className="flex flex-col sm:gap-8 md:flex-row mobile:space-y-8">
            <TextField id="email" label="Email *" name="email" placeholder="Entrer mon email" fullWidth type="email" control={control} value={email} />
            <TextField
              id="password"
              label="Mot de passe *"
              name="password"
              placeholder="Entrer mon mot de passe"
              helperText="Le mot de passe doit contenir au moins 10 caractères avec au moins une minuscule, une majuscule et un chiffre."
              fullWidth
              type="password"
              control={control}
              value={password}
            />
          </div>
          <div className="flex flex-col sm:gap-8 md:flex-row mobile:space-y-8">
            <PhoneInput
              id="mobile"
              label="Mobile *"
              name="mobile"
              placeholder="Entrer mon numéro de mobile"
              type="tel"
              fullWidth
              country="NC"
              inputComponent={SimpleTextField}
              control={control}
              value={mobile}
            />
            <PhoneInput
              id="phone"
              label="Téléphone"
              name="phone"
              placeholder="Entrer mon numéro de téléphone"
              type="tel"
              fullWidth
              country="NC"
              inputComponent={SimpleTextField}
              control={control}
              value={phone}
            />
          </div>
        </div>

        {/*<div className='flex md:flex-row flex-col md:mx-0 mx-[30px] space-y-8 mt-12.5'>*/}
        {/*  <Controller*/}
        {/*    name='home_address'*/}
        {/*    control={ control }*/}
        {/*    render={ ({ field: { onChange } }) => (*/}
        {/*      <Address*/}
        {/*        key='home_address'*/}
        {/*        label='Maison'*/}
        {/*        onChange={ onChange }*/}
        {/*      />*/}
        {/*    )}*/}
        {/*  />*/}
        {/*  <Controller*/}
        {/*    name='office_address'*/}
        {/*    control={ control }*/}
        {/*    render={ ({ field: { onChange } }) => (*/}
        {/*      <Address*/}
        {/*        key='office_address'*/}
        {/*        label='Bureau'*/}
        {/*        organization*/}
        {/*        onChange={ onChange }*/}
        {/*      />*/}
        {/*    )}*/}
        {/*  />*/}
        {/*</div>*/}

        {/*<Divider className="mt-12.5 md:mx-0 mx-[30px]" />*/}

        <div className="mt-8 flex flex-col items-center space-y-8">
          <div className="flex flex-row items-center">
            <StyledCheckbox required name="cgu" checked={checked} onChange={event => setChecked(event.target.checked)} color="primary" />
            <Typography className="cursor-pointer select-none" onClick={() => setChecked(!checked)}>
              J'accepte les{' '}
              <Link href="/cgv" target="_blank" className="font-bold text-super-ya-blue3" onClick={event => event.stopPropagation()}>
                conditions générales de vente
              </Link>{' '}
              *
            </Typography>
          </div>

          <div className="flex flex-col space-y-8">
            <FlatButton variant="contained" disabled={status === 'loading'} color="primary" type="submit" className="max-w-[350px] font-bold normal-case sm:w-[350px]">
              {status === 'loading' ? (
                <React.Fragment>
                  {' '}
                  <CircularProgress size={20} /> <span className="ml-2 text-gray-600">Création en cours...</span>
                </React.Fragment>
              ) : (
                'Créer mon compte'
              )}
            </FlatButton>
            <Typography>* Tous les champs sont obligatoires</Typography>
          </div>
        </div>
      </form>
    </div>
  )
}

const Layout = withProps(ArticleLayout, {
  banner: (
    <div className="mx-auto sm:w-[100%] mobile:w-11/12">
      <AlertInfo>
        <Typography>
          Attention, si vous possédiez un compte Super-Ya sur l'ancien site, veuillez utiliser les mêmes identifiants.
          <br />
          Vous avez oublié ces identifiants ? N'hésitez pas à nous contacter en cliquant ici&nbsp;
          <Link href="/contact" className="whitespace-nowrap underline hover:text-white">
            Contactez-nous
          </Link>
        </Typography>
      </AlertInfo>
    </div>
  ),
  title: 'Je crée mon compte',
  subtitle: 'Mon compte me permet de gérer mes commandes ainsi que mes informations.',
  additional: (
    <div className="mx-auto mt-[50px] mobile:w-11/12">
      <Footer help="J'ai déjà un compte Super Ya ?" action="Je me connecte !" ctaLabel="Se connecter à mon compte" ctaTarget={routes.connexion.uri} />
    </div>
  )
})

export default withLayout(withAnon(SignUp), Layout)
